import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  SparklesIcon,
  ArrowUpCircleIcon,
  LanguageIcon,
  ChatBubbleBottomCenterTextIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from "@mindstonehq/ui";

import React from "react";
import { v4 as uuidv4 } from "uuid";
import { AiDelta } from "./ai-delta";
import {
  AIMessage,
  AiMessageRole,
  IHighlightCommon,
  PopoverOptionAskAI,
} from "~/typings/annotate/annotation";
import { useHighlightContext } from "../hooks/use-selected-highlight";
import { ListPlusIcon, NoteIcon } from "~/components/icons";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export default function AskingAI({
  config,
  highlight,
}: {
  config: PopoverOptionAskAI;
  highlight: IHighlightCommon;
}) {
  const { t } = useLingui();
  const [customPrompt, setCustomPrompt] = React.useState("");
  const [messages, setMessages] = React.useState<Array<AIMessage>>([]);
  const [loading, setLoading] = React.useState(false);
  const [carousel, setCarousel] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [showLanguageSelector, setShowLanguageSelector] = React.useState(false);
  const aiMessages = messages.filter((m) => m.role === AiMessageRole.Assistant);

  React.useEffect(() => {
    if (!carousel) return;
    setCurrent(carousel.selectedScrollSnap() + 1);
    carousel.on("select", () => {
      setCurrent(carousel.selectedScrollSnap() + 1);
    });
  }, [carousel]);

  const handleResponseInProgress = (
    idempotencyID: string,
    userContent: string,
  ) => {
    // Set messages in progress, which we replace once the full response is back
    setLoading(true);
    setMessages((prev) => {
      const updatedResponses = [...prev];
      const user = {
        role: AiMessageRole.User,
        idempotencyID,
        content: userContent,
      };
      const assistant = {
        role: AiMessageRole.Assistant,
        idempotencyID,
        content: "",
      };
      if (updatedResponses.length > 0) {
        const last = updatedResponses[updatedResponses.length - 1];
        if (last.role === AiMessageRole.User) {
          // We already have a user one, we need to add just the assistant
          updatedResponses.push(assistant);
        } else {
          // We need to add the pair otherwise
          updatedResponses.push(user, assistant);
        }
      } else {
        // It's our first message, we need to add the pair
        updatedResponses.push(user, assistant);
      }

      setTimeout(() => {
        const updatedAIResponses = updatedResponses.filter(
          (r) => r.role === AiMessageRole.Assistant,
        );
        carousel?.scrollTo(updatedAIResponses.length - 1);
      }, 1000);
      return updatedResponses;
    });
  };

  // Returns true if a matching assistant placeholder was successfully
  // replaced, false otherwise (e.g. empty respMessages or no matching
  // role). Callers fall through to handleResponseError on false so the
  // placeholder doesn't stay parked on <AiDelta> waiting for Redis
  // frames forever.
  //
  // Immutable update: previously this mutated `prev` in-place and
  // returned the same array reference, so React would only re-render
  // because setLoading(false) followed. Under batched updates that's
  // brittle — clearing idempotencyID can fail to take effect if loading
  // is already false. Build a fresh array via .map.
  const handleResponse = (
    idempotencyID: string,
    respMessages: AIMessage[],
  ): boolean => {
    setLoading(true);
    let replaced = false;
    setMessages((prev) =>
      prev.map((m) => {
        const match = respMessages.find(
          (rm) => rm.role === m.role && m.idempotencyID === idempotencyID,
        );
        if (match) {
          replaced = true;
          return { ...match, idempotencyID: undefined };
        }
        return m;
      }),
    );
    setLoading(false);
    return replaced;
  };

  // Mutation rejected, OR resolved without our expected role in
  // respMessages. The placeholder would otherwise stay parked on
  // <AiDelta> waiting for ai_delta_<idempotencyID> frames that won't
  // come. Replace it with an error notice and clear idempotencyID so
  // the streaming child unmounts.
  const handleResponseError = (idempotencyID: string) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.idempotencyID === idempotencyID && m.role === AiMessageRole.Assistant
          ? {
              ...m,
              content: t(msg`Sorry, something went wrong. Please try again.`),
              idempotencyID: undefined,
            }
          : m,
      ),
    );
    setLoading(false);
  };

  const handleSummarize = () => {
    const idempotencyID = uuidv4();
    handleResponseInProgress(idempotencyID, t(msg`Summarise`));
    config
      .aiSummarise(highlight.text, messages, idempotencyID)
      .then((resp) => {
        if (!handleResponse(idempotencyID, resp?.messages || [])) {
          handleResponseError(idempotencyID);
        }
      })
      .catch((err) => {
        console.error("aiSummarise failed", err);
        handleResponseError(idempotencyID);
      });
  };

  const handleSimplify = () => {
    const idempotencyID = uuidv4();
    handleResponseInProgress(idempotencyID, t(msg`Simplify`));
    config
      .aiSimplify(highlight.text, messages, idempotencyID)
      .then((resp) => {
        if (!handleResponse(idempotencyID, resp?.messages || [])) {
          handleResponseError(idempotencyID);
        }
      })
      .catch((err) => {
        console.error("aiSimplify failed", err);
        handleResponseError(idempotencyID);
      });
  };

  const handleTranslate = (language: string) => {
    const idempotencyID = uuidv4();
    handleResponseInProgress(idempotencyID, t(msg`Translate into ${language}`));
    config
      .aiTranslate(highlight.text, messages, language, idempotencyID)
      .then((resp) => {
        if (!handleResponse(idempotencyID, resp?.messages || [])) {
          handleResponseError(idempotencyID);
        }
      })
      .catch((err) => {
        console.error("aiTranslate failed", err);
        handleResponseError(idempotencyID);
      });
  };

  const translateClicked = () => {
    setShowLanguageSelector(true);
  };

  const handleWrittenPromptSubmit = () => {
    const idempotencyID = uuidv4();
    const prompt = String(customPrompt);
    setCustomPrompt("");
    handleResponseInProgress(idempotencyID, prompt);
    config
      .askAI(highlight.text, messages, prompt, idempotencyID)
      .then((resp) => {
        if (!handleResponse(idempotencyID, resp?.messages || [])) {
          handleResponseError(idempotencyID);
        }
      })
      .catch((err) => {
        console.error("askAI failed", err);
        handleResponseError(idempotencyID);
      });
  };

  const [value, setValue] = React.useState<string>("");

  const { openComments, openNote } = useHighlightContext();

  const handleSaveAsNote = () => {
    openNote(highlight, aiMessages.slice(-1)[0].content); // With the content of the last message
  };
  const handleSaveAsComment = () => {
    openComments(highlight, aiMessages.slice(-1)[0].content); // With the content of the last message
  };
  const handleTryAgain = () => {
    // Since we return the constructed user messages from all mutations,
    // When retrying it's enough to pop the last assistant message and call the generic askAI mutation
    messages.pop(); // Remove the last assistant message
    handleWrittenPromptSubmit();
  };

  return (
    <Command
      shouldFilter={false}
      className="w-[440px]"
      value={value}
      onValueChange={setValue}
    >
      {!!aiMessages.length && (
        <>
          <Carousel setApi={setCarousel} className="w-full">
            <CarouselContent>
              {aiMessages.map((response, index) => (
                <CarouselItem key={index}>
                  <div className="p-4 text-sm max-h-[200px] overflow-y-auto whitespace-pre-wrap">
                    {/*
                      aiDelta fallback. The ask-AI mutations (aiAsk /
                      aiSummarise / aiSimplify / aiTranslate) all return the
                      full assistant text in their response; handleResponse
                      writes it into messages[].content AND clears
                      idempotencyID. Before this fix the carousel rendered
                      <AiDelta> unconditionally, which subscribes to
                      ai_delta_<idempotencyID> via GraphQL WS and waits for
                      streamed frames — a plain Redis pub/sub channel
                      (server/core/redis/client.ts) that drops frames when
                      the subscription opens after the publisher finished,
                      so the popover bubble would stay empty even though
                      the mutation succeeded. Now we use idempotencyID as
                      the "still streaming" signal: present → mount
                      <AiDelta> for the typing effect; cleared by
                      handleResponse / handleResponseError → render
                      content directly. Same pattern as 7dc23f99c0 for
                      /personalizer/chat.
                    */}
                    {response.idempotencyID ? (
                      <AiDelta
                        idempotencyID={response.idempotencyID}
                        useAIDelta={config.useAIDelta}
                      />
                    ) : (
                      <span>{response.content}</span>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {aiMessages.length > 1 && (
            <div className="flex justify-center text-sm space-x-2">
              <button
                onClick={() => {
                  if (carousel) carousel.scrollPrev();
                }}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <div>
                <Trans>{current} of {aiMessages.length}</Trans>
              </div>
              <button
                onClick={() => {
                  if (carousel) carousel.scrollNext();
                }}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
      <div className="relative">
        <CommandInput
          className="border-0 active:border-0 focus:border-0 focus:shadow-none focus:ring-0 focus:outline-0 w-full pr-12"
          value={customPrompt}
          onValueChange={setCustomPrompt}
          disabled={loading}
          placeholder={
            loading
              ? t(msg`AI is working...`)
              : messages.length
                ? t(msg`Tell AI what to do next...`)
                : t(msg`Ask AI`)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && customPrompt) {
              e.preventDefault();
              // User has written something, do that
              return handleWrittenPromptSubmit();
            }
          }}
        />
        <div className="w-4 h-full absolute right-4 top-0 flex items-center">
          {!!customPrompt && (
            <button onClick={handleWrittenPromptSubmit}>
              <ArrowUpCircleIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {!messages.length && !loading && !customPrompt && (
        <CommandList>
          <CommandEmpty><Trans>No results found.</Trans></CommandEmpty>
          <CommandGroup
            heading={t(msg`Suggestions`)}
            className="max-h-[150px] overflow-y-auto"
          >
            {showLanguageSelector ? (
              <>
                {config.aiTranslateLanguages.map((language) => (
                  <CommandItem
                    key={language}
                    onSelect={() => handleTranslate(value)}
                    className="cursor-pointer capitalize"
                  >
                    <span>{language}</span>
                  </CommandItem>
                ))}
              </>
            ) : (
              <>
                <CommandItem
                  onSelect={handleSummarize}
                  className="cursor-pointer"
                >
                  <ListPlusIcon className="mr-2 h-4 w-4" />
                  <span><Trans>Summarize</Trans></span>
                </CommandItem>
                <CommandItem
                  onSelect={handleSimplify}
                  className="cursor-pointer"
                >
                  <SparklesIcon className="mr-2 h-4 w-4" />
                  <span><Trans>Simplify Meaning</Trans></span>
                </CommandItem>
                <CommandItem
                  onSelect={translateClicked}
                  className="cursor-pointer"
                >
                  <LanguageIcon className="mr-2 h-4 w-4" />
                  <span><Trans>Translate</Trans></span>
                </CommandItem>
              </>
            )}
          </CommandGroup>
        </CommandList>
      )}
      {messages.length > 0 && !loading && (
        <CommandList>
          <CommandGroup
            heading={t(msg`Actions`)}
            className="max-h-[150px] overflow-y-auto"
          >
            <CommandItem onSelect={handleSaveAsNote} className="cursor-pointer">
              <NoteIcon className="mr-2 h-4 w-4" />
              <span><Trans>Save as private note</Trans></span>
            </CommandItem>
            <CommandItem
              onSelect={handleSaveAsComment}
              className="cursor-pointer"
            >
              <ChatBubbleBottomCenterTextIcon className="mr-2 h-4 w-4" />
              <span><Trans>Post as public comment</Trans></span>
            </CommandItem>
            <CommandItem onSelect={handleTryAgain} className="cursor-pointer">
              <ArrowPathIcon className="mr-2 h-4 w-4" />
              <span><Trans>Try again</Trans></span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      )}
    </Command>
  );
}
